import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { MagnifyingGlass, Trash, ArrowsClockwise, ChartLineUp, Globe } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SeoRankPage = () => {
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  const [checkingAll, setCheckingAll] = useState(false);

  const authHeader = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchKeywords = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/seo-rank/keywords`, { headers: authHeader() });
      setKeywords(data);
    } catch (e) {
      toast.error('Erreur de chargement des mots-clés');
    }
  };

  const fetchUsage = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/seo-rank/usage`, { headers: authHeader() });
      setUsage(data);
    } catch (e) {
      // silently ignore (likely missing key)
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchKeywords(), fetchUsage()]);
      setLoading(false);
    })();
  }, []);

  const addKeyword = async (e) => {
    e?.preventDefault();
    if (!newKeyword.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/admin/seo-rank/keywords`,
        { keyword: newKeyword.trim() },
        { headers: authHeader() }
      );
      setNewKeyword('');
      await fetchKeywords();
      toast.success('Mot-clé ajouté');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur ajout');
    } finally {
      setAdding(false);
    }
  };

  const deleteKeyword = async (id) => {
    if (!window.confirm('Supprimer ce mot-clé et son historique ?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/seo-rank/keywords/${id}`, { headers: authHeader() });
      await fetchKeywords();
      toast.success('Supprimé');
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  const checkOne = async (id) => {
    setCheckingId(id);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/seo-rank/keywords/${id}/check`, null, {
        headers: authHeader(),
      });
      toast.success(data.position ? `Position #${data.position}` : 'Hors top 100');
      await Promise.all([fetchKeywords(), fetchUsage()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur vérification');
    } finally {
      setCheckingId(null);
    }
  };

  const checkAll = async () => {
    if (!window.confirm(`Vérifier ${keywords.length} mots-clés ? Utilise les crédits SerpApi (cache 24h actif).`)) return;
    setCheckingAll(true);
    try {
      await axios.post(`${API_URL}/api/admin/seo-rank/keywords/check`, null, {
        headers: authHeader(),
        params: { force: false },
      });
      toast.success('Vérification batch terminée');
      await Promise.all([fetchKeywords(), fetchUsage()]);
    } catch (err) {
      toast.error('Erreur vérification batch');
    } finally {
      setCheckingAll(false);
    }
  };

  const renderRank = (p) => {
    if (p == null) return <Badge variant="outline" className="text-slate-500">Hors top 100</Badge>;
    if (p <= 3) return <Badge className="bg-green-600">#{p}</Badge>;
    if (p <= 10) return <Badge className="bg-emerald-500">#{p}</Badge>;
    if (p <= 30) return <Badge className="bg-amber-500">#{p}</Badge>;
    return <Badge className="bg-slate-400">#{p}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ChartLineUp size={28} /> Suivi de positions Google
        </h1>
        <p className="text-slate-500 text-sm">Surveillez votre classement sur Google France pour vos mots-clés stratégiques.</p>
      </div>

      {/* Usage / SerpApi status */}
      {usage && (
        <Card className="dashboard-card">
          <CardContent className="pt-6 flex flex-wrap gap-6 items-center justify-between">
            <div>
              <p className="text-xs uppercase text-slate-500">Plan SerpApi</p>
              <p className="font-semibold" data-testid="serpapi-plan">{usage.plan_name}</p>
            </div>
            <div>
              <p className="text-xs uppercase text-slate-500">Recherches restantes</p>
              <p className="font-semibold" data-testid="serpapi-left">
                {usage.total_searches_left} / {usage.searches_per_month}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase text-slate-500">Utilisées ce mois</p>
              <p className="font-semibold">{usage.this_month_usage}</p>
            </div>
            <a href="https://serpapi.com/dashboard" target="_blank" rel="noopener noreferrer"
               className="text-sm text-[#0052FF] hover:underline">→ Dashboard SerpApi</a>
          </CardContent>
        </Card>
      )}

      {/* Add keyword */}
      <Card className="dashboard-card">
        <CardHeader>
          <CardTitle>Ajouter un mot-clé</CardTitle>
          <CardDescription>Suivi sur google.fr — France, langue française, top 100.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={addKeyword} className="flex gap-2">
            <Input
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              placeholder="ex: chorizo ibérique en ligne"
              data-testid="seo-rank-new-input"
              className="flex-1"
            />
            <Button type="submit" disabled={adding || !newKeyword.trim()} data-testid="seo-rank-add-btn">
              {adding ? 'Ajout…' : 'Ajouter'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Keywords table */}
      <Card className="dashboard-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Mes mots-clés ({keywords.length})</CardTitle>
            <CardDescription>Cache 24h pour préserver les crédits SerpApi.</CardDescription>
          </div>
          <Button onClick={checkAll} disabled={checkingAll || keywords.length === 0}
                  className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="seo-rank-check-all">
            <ArrowsClockwise size={18} className={checkingAll ? 'animate-spin mr-2' : 'mr-2'} />
            {checkingAll ? 'Vérification…' : 'Tout vérifier'}
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-slate-500">Chargement…</p>
          ) : keywords.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <MagnifyingGlass size={48} className="mx-auto mb-3 text-slate-300" />
              <p>Aucun mot-clé. Commencez par en ajouter un ci-dessus.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-slate-500">
                    <th className="py-2 px-2">Mot-clé</th>
                    <th className="py-2 px-2">Position</th>
                    <th className="py-2 px-2">URL trouvée</th>
                    <th className="py-2 px-2">Dernière vérif.</th>
                    <th className="py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {keywords.map((k) => (
                    <tr key={k.id} className="border-b hover:bg-slate-50" data-testid={`seo-rank-row-${k.id}`}>
                      <td className="py-3 px-2 font-medium">{k.keyword}</td>
                      <td className="py-3 px-2">{renderRank(k.last_position)}</td>
                      <td className="py-3 px-2 max-w-xs truncate">
                        {k.last_matched_url ? (
                          <a href={k.last_matched_url} target="_blank" rel="noopener noreferrer"
                             className="text-[#0052FF] hover:underline inline-flex items-center gap-1">
                            <Globe size={14} /> {k.last_matched_url.replace(/^https?:\/\//, '').slice(0, 40)}…
                          </a>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="py-3 px-2 text-slate-500">
                        {k.last_checked_at ? new Date(k.last_checked_at).toLocaleString('fr-FR') : '—'}
                      </td>
                      <td className="py-3 px-2 flex gap-1 justify-end">
                        <Button
                          variant="outline" size="sm"
                          onClick={() => checkOne(k.id)}
                          disabled={checkingId === k.id}
                          data-testid={`seo-rank-check-${k.id}`}
                        >
                          <ArrowsClockwise size={14} className={checkingId === k.id ? 'animate-spin' : ''} />
                        </Button>
                        <Button
                          variant="outline" size="sm"
                          onClick={() => deleteKeyword(k.id)}
                          data-testid={`seo-rank-delete-${k.id}`}
                        >
                          <Trash size={14} className="text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SeoRankPage;
